import { useState } from "react";
import { exportData, libraryListExport, listData, loadLibraryData, saveLibraryData } from "../../../master/libraryData";
import { Pack } from "../../../master/data";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";
import { CharacterSpecies, CompanionWeapon, Equipment, Outfit, OutfitBlock, PackData, SaveableBase, Trait, Weapon, WeaponBlock } from "../../../model/character";
import { Modal } from "../../../components/Modal";
import Section from "../../../components/Section";

import AddIcon from '@mui/icons-material/Add';
import Page from "../../../components/Page";
import { Box, Checkbox, Stack } from "@mui/material";
import ButtonFlat from "../../../components/ButtonFlat";
import { TextField } from "../../../components/formFields/TextField";
import FormFieldLabel from "../../../components/formFields/FormFieldLabel";

const defaultPack: Pack = {
    id: "",
    name: "",
    miscItems: [],
    outfitMods: [],
    outfits: [],
    outfitVariations: [],
    utilities: [],
    weaponMods: [],
    weapons: [],
    weaponVariations: [],
    species: [],
    companionWeapons: [],
    perks: [],
    traits: [],
    complications: [],
}

export const GetNew = (name: string): Pack => {
    return {
        ...defaultPack,
        id: v4(),
        name: name
    }
}

interface ListModalProps {
    list: SaveableBase[];
    open: boolean;
    title: string;
    selection: string[];
    onClose: () => void;
    onChange: (id: string, state: boolean) => void;
    onChangeMany: (ids: string[], state: boolean) => void;
}

const ListModal = (props: ListModalProps) => {
    const [ filter, setFilter ] = useState("");
    const filtered = props.list.filter((i) => i.name.toLowerCase().includes(filter.toLowerCase()))
    const all = filtered.length === props.selection.length;
    
    const handleSelectAll = (value: boolean) => {
        props.onChangeMany(filtered.map(f => f.id), value);
    }


    return <Modal
        actions={[
            {
                label: "Close",
                onClick: props.onClose,
                size: 12,
            }
        ]}
        onClose={() => undefined} open={props.open} title={`Select ${props.title}`}> 

        <Stack direction={"row"} spacing={2}>
            <TextField placeholder="Search" value={filter} onChange={setFilter} /> <Checkbox checked={all} onChange={(e) => handleSelectAll(e.target.checked)} />
        </Stack>
        
        
        
        {filtered.map((item) => <div key={item.id}>
            <Checkbox checked={props.selection.includes(item.id)} onChange={(e) => props.onChange(item.id, e.target.checked)} /> {item.name}
        </div>)}
    </Modal>
}

interface LibraryListProps {
    title: string;
    items: SaveableBase[];
    selection: string[];
    onChange: (selection: string[]) => void;
}

const LibraryList = (props: LibraryListProps) =>{
    const [ modal, setModal ] = useState(false);
    const filteredItems = props.items.filter(i => props.selection.includes(i.id) )

    const onChange = (id: string, checked: boolean) => {
        if(checked){
            if(!props.selection.includes(id)){
                props.onChange([
                    ...props.selection,
                    id
                ]);
            }
        }else{
            props.onChange([...props.selection.filter(s => s !== id)]);
        }
    }

    const onChangeMany = (ids: string[], checked: boolean) => {
        if(checked){
            const onlyNew = ids.filter(id => !props.selection.includes(id));

            props.onChange([...props.selection, ...onlyNew]);
        }else{
            props.onChange([...props.selection.filter(s => !ids.includes(s))]);
        }
    }

    return <Section title={props.title} actions={[
        {
            action: () => setModal(true),
            label: <AddIcon></AddIcon>
        }
    ]}>
        <Box paddingLeft={1}>
            {filteredItems.map((item) => item.name).join(", ")}
        </Box>
        <ListModal onChange={onChange} onChangeMany={onChangeMany} selection={props.selection} open={modal} list={props.items} title={`Manage ${props.title}`} onClose={() => setModal(false)} />
    </Section>
}

export const PackManager = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ current, setCurrent ] = useState<Pack>(
        { ...defaultPack,
        ...(loadLibraryData<Pack>('lp', id ?? "NONE") ?? GetNew("New Pack"))
    });

    const onChange = (name: string, data: SaveableBase[], selection: string[]) => {
        setCurrent((old) => {
            const dataIds = data.map(d => d.id);
            return {
                ...old,
                [name]: selection.filter(f => dataIds.includes(f))
            };
        })
    }

    const onChangeValue = (key: string, value: any) => {
        setCurrent((old) => {
            return {
                ...old,
                [key]: value
            }
        });
    }

    const handleSave = () => {
        navigate(`/library/pack/${current.id}`, {replace: true});
        saveLibraryData("lp", current);
    }

    const handleBack = () => {
        navigate("/library");
    }
    
    const weaponMods = listData("lwm");
    const weaponVars = listData("lwv");
    const weapons = listData("lw");

    const outfitMods = listData("lom");
    const outfitVars = listData("lov");
    const outfits = listData("lo");

    const species = listData("lspec");

    const companionWeapons = listData("lcomwep");

    const miscItems = listData("lmi");
    const utilities = listData("lut");
    const tradegoods = listData("tg");

    const traits = listData("ltr");
    const perks = listData("lpe");
    const complications = listData("lco");

    

    const handleExport = () => {
        const exportWeapons = libraryListExport<WeaponBlock>("lw", current.weapons);
        const exportWeaponMods = libraryListExport<WeaponBlock>("lwm", current.weaponMods);
        const exportWeaponVars = libraryListExport<WeaponBlock>("lwv", current.weaponVariations);

        const exportOutfits = libraryListExport<OutfitBlock>("lo", current.outfits);
        const exportOutfitMods = libraryListExport<OutfitBlock>("lom", current.outfitMods);
        const exportOutfitVars = libraryListExport<OutfitBlock>("lov", current.outfitVariations);

        const exportUtilities = libraryListExport<Equipment>("lut", current.utilities);
        const exportMiscItems = libraryListExport<Equipment>("lmi", current.miscItems);

        const exportTraits = libraryListExport<Trait>("ltr", current.traits);
        const exportPerks = libraryListExport<Trait>("lpe", current.perks);
        const exportComplications = libraryListExport<Trait>("lco", current.complications);

        const exportSpecies = libraryListExport<CharacterSpecies>("lspec", current.species);

        const exportCompanionWeapon = libraryListExport<CompanionWeapon>("lcomwep", current.companionWeapons);

        const packExport: PackData = {
            id: current.id,
            name: current.name,
            weapons: exportWeapons,
            weaponMods: exportWeaponMods,
            weaponVariations: exportWeaponVars,
            outfits: exportOutfits,
            outfitMods: exportOutfitMods,
            outfitVariation: exportOutfitVars,
            utilities: exportUtilities,
            miscItems: exportMiscItems,
            species: exportSpecies,
            companionWeapons: exportCompanionWeapon,
            traits: exportTraits,
            perks: exportPerks,
            complications: exportComplications
        }
        exportData(packExport, "lp");
    }

    return <Page>
        <h2>{current.name}</h2>

        <Stack spacing={1}>
            <FormFieldLabel label="Name">
                <TextField value={current.name} onChange={(value) => onChangeValue("name", value)} />
            </FormFieldLabel>
            <LibraryList onChange={(s) => onChange("weapons", weapons, s)} items={weapons} selection={current.weapons} title="Weapons" />
            <LibraryList onChange={(s) => onChange("weaponMods", weaponMods, s)} items={weaponMods} selection={current.weaponMods} title="Weapon Mods" />
            <LibraryList onChange={(s) => onChange("weaponVariations", weaponVars, s)} items={weaponVars} selection={current.weaponVariations} title="Weapon Variations" />
 
            <LibraryList onChange={(s) => onChange("outfits", outfits, s)} items={outfits} selection={current.outfits} title="Outfits" />
            <LibraryList onChange={(s) => onChange("outfitMods", outfitMods, s)} items={outfitMods} selection={current.outfitMods} title="Outfit Mods" />
            <LibraryList onChange={(s) => onChange("outfitVariations", outfitVars, s)} items={outfitVars} selection={current.outfitVariations} title="Outfit Variations" />

            <LibraryList onChange={(s) => onChange("species", species, s)} items={species} selection={current.species} title="Species" />
 
            <LibraryList onChange={(s) => onChange("miscItems", miscItems, s)} items={miscItems} selection={current.miscItems} title="Misc Items" />
            <LibraryList onChange={(s) => onChange("utilities", utilities, s)} items={utilities} selection={current.utilities} title="Utilities" />
            {/* <LibraryList items={weaponVars} selection={current.weaponVariations} title="Weapon Variations" /> */}

            <LibraryList onChange={(s) => onChange("companionWeapons", companionWeapons, s)} items={companionWeapons} selection={current.companionWeapons} title="Companion (Weapons)" />

            <LibraryList onChange={(s) => onChange("traits", traits, s)} items={traits} selection={current.traits} title="Traits" />
            <LibraryList onChange={(s) => onChange("perks", perks, s)} items={perks} selection={current.perks} title="Perks" />
            <LibraryList onChange={(s) => onChange("complications", complications, s)} items={complications} selection={current.complications} title="Complications" />

            <Stack direction={"row"} spacing={1} paddingTop={1}>
                <ButtonFlat onClick={handleSave}>Save</ButtonFlat>
                <ButtonFlat onClick={handleBack}>Back to Library</ButtonFlat>
                <ButtonFlat onClick={handleExport}>Export</ButtonFlat>
            </Stack>
        </Stack>
    </Page>
}